import {getTestProps} from '@benchling/js-test-utils';
import {datadogRum} from '@datadog/browser-rum';
import classNames from 'classnames';
import React, {useEffect} from 'react';
import ReactDOM from 'react-dom';

import * as benchlingUtil from 'BenchlingUtil';
import {isBrowser} from 'lib/Browser';
import * as MediocreConstants from 'notebook/mediocre/MediocreConstants';
import {PaperSize, PrintOrientation} from 'notebook/mediocre/print/PrintConstants';
import PrintOptionsModal from 'notebook/mediocre/print/PrintOptionsModal';
import * as PrintUtil from 'notebook/mediocre/print/PrintUtil';
import {TIMESTAMP_WIDTH} from 'notebook/notes/NoteConstants';
import {loadingIndicatorAttrs, queryAllLoadingIndicators} from 'notebook/util/ExportInstrumentation';
import ModalAnchor from 'ui-library/ModalAnchor';
import Spinner from 'ui-library/Spinner';
import * as keyboard from 'util/Keyboard';

/**
 * PrintWrapper is a React component that wraps another React component for print:
 *  - Renders a loading screen while there is no component.
 *  - Keeps the loading screen (in browser environment) if a font resource or any <img> resource is
 *    not yet loaded.
 *  - Triggers the print behavior or via Ctrl-P after loading if the current URL's hash is #print
 *    (in browser environment)
 *  - Sizes / orients tables so that they fit-to-width before print
 *
 * The wrapped component may use <div className='printable-page-container'> to introduce linebreaks between
 * logical pages.
 */

interface PrintableWrapperProps {
  title?: string;
  children: React.ReactNode;
  shouldPadForTimestamps?: boolean; // Will add additional padding to the page to accomodate space for timestamps
}

interface State {
  areFileResourcesLoaded: boolean;
  isDOMLoaded: boolean;
  style: JSX.Element | null;
  isOptionsModalOpen: boolean;
  showLinkURLs: boolean;
  printableWidth: number;
}

function isLoadingFile(props: PrintableWrapperProps): boolean {
  return React.Children.count(props.children) === 0;
}

function isLoadingResources(props: PrintableWrapperProps, state: State): boolean {
  return isLoadingFile(props) || !state.areFileResourcesLoaded || !state.isDOMLoaded;
}

const DEFAULT_PAGE_DIMENSIONS_FOR_HEADLESS_CHROME_EXPORTS = PrintUtil.getPageDims(
  PaperSize.US_LETTER,
  PrintOrientation.PORTRAIT
);

interface LoadingIndicatorWatcherProps {
  containerRef: HTMLElement | null;
  onLoadingComplete: () => void;
}

function LoadingIndicatorWatcher({
  containerRef,
  onLoadingComplete,
}: LoadingIndicatorWatcherProps): JSX.Element | null {
  useEffect(() => {
    const interval = setInterval(() => {
      if (containerRef) {
        const loadingIndicators = queryAllLoadingIndicators(containerRef);
        if (loadingIndicators.length === 0) {
          onLoadingComplete();
          clearInterval(interval);
        }
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [containerRef, onLoadingComplete]);

  return null;
}

class PrintableWrapper extends React.Component<PrintableWrapperProps, State> {
  override state: State = {
    areFileResourcesLoaded: !isBrowser,
    isDOMLoaded: !isBrowser,
    style: null,
    isOptionsModalOpen: false,
    showLinkURLs: false, // can be overridden by PrintOptionsModal
    printableWidth: 0,
  };

  _isMounted = false;
  contentRef = React.createRef<HTMLDivElement>();

  override componentDidMount(): void {
    this._isMounted = true;
    if (isBrowser) {
      this._waitForInitialDOMLoad();
      benchlingUtil.setWindowTitle(this.props.title);
      document.addEventListener('keydown', this._handleKeyDownForPrint);
    }
  }

  override componentDidUpdate(prevProps: PrintableWrapperProps, prevState: State): void {
    if (isBrowser) {
      // If the title changes (unset to set), set it!
      if (prevProps.title !== this.props.title) {
        benchlingUtil.setWindowTitle(this.props.title);
      }
    }
  }

  override componentWillUnmount(): void {
    if (isBrowser) {
      document.removeEventListener('keydown', this._handleKeyDownForPrint);
    }
    this._isMounted = false;
  }

  _waitForInitialDOMLoad(): void {
    if (document.readyState === 'complete') {
      this.setState({isDOMLoaded: true});
    } else {
      window.addEventListener('load', () => {
        if (!this._isMounted) {
          return;
        }
        this.setState({isDOMLoaded: true});
      });
    }
  }

  _handleKeyDownForPrint = (e: KeyboardEvent): void => {
    if (keyboard.isCtrlP(e)) {
      e.preventDefault();
      if (!isLoadingResources(this.props, this.state)) {
        this._openOptionsModal();
      }
    }
  };

  _openOptionsModal(): void {
    this.setState({isOptionsModalOpen: true});
  }

  _closeOptionsModal = (): void => {
    this.setState({isOptionsModalOpen: false});
  };

  _print = ({
    shouldFitToWidth,
    dimensions,
    showLinkURLs,
  }: {
    shouldFitToWidth: boolean;
    dimensions: PrintUtil.PageDims;
    showLinkURLs: boolean;
  }): void => {
    let printableWidth = this.props.shouldPadForTimestamps
      ? dimensions.width - TIMESTAMP_WIDTH
      : dimensions.width;
    printableWidth = shouldFitToWidth ? 0 : printableWidth;
    this.setState(
      {
        style: PrintUtil.getFitToWidthStyle(
          ReactDOM.findDOMNode(this) as Element,
          dimensions,
          shouldFitToWidth
        ),
        isOptionsModalOpen: false,
        showLinkURLs,
        printableWidth,
      },
      () => {
        // XXX: We want NoteEditor to rerender to reflect any changes in the width of the printable
        // container. This is required to force CaptionEditables to render the caption location below the
        // captioned content when printing in portrait mode. NoteEditor is memoized but responds to
        // browser window resize events, so we simulate one here.
        window.dispatchEvent(new Event('resize'));
        // Delay opening the OS's print preview modal until after we've had a chance to rerender.
        // XXX: We need to yield 25ms before opening the browser print modal because dismissing
        // the print options modal is debounced, meaning it requires ticks to disappear from the DOM.
        // We arbitrarly set up 25ms since it's not too big of a delay and gives us enough time to close out
        // the modal here.
        setTimeout(() => {
          window.print();
        }, 25);
      }
    );
  };

  override render(): JSX.Element {
    const hasResourcesLoading = isLoadingResources(this.props, this.state);
    const hasFileLoading = isLoadingFile(this.props);
    return (
      <>
        {!hasResourcesLoading ? null : (
          <div className="loading-spinner-background fullscreen-msg" {...loadingIndicatorAttrs()}>
            <div className="loading-text">
              {hasFileLoading ? 'preparing for print...' : 'loading file...'}
            </div>
            <Spinner />
          </div>
        )}
        <div ref={this.contentRef}>
          <div
            // 'printable-container' is the DOM marker that headless browser exports
            // use to detect React mount success — render_failure_checks.py::PRINTABLE_CONTAINER_SELECTOR.
            // Do not rename without updating that constant.
            className={classNames('printable-container', {
              'is-loading': hasResourcesLoading,
              'hide-print-links': !this.state.showLinkURLs,
            })}
            {...getTestProps({component: 'PrintableWrapper', element: 'container'})}
            data-printable-left-margin={MediocreConstants.MEDIOCRE_PAGE_MARGIN_PX}
            style={this.state.printableWidth > 0 ? {width: `${this.state.printableWidth}px`} : undefined}
          >
            <div className="printable-container-boundingBox">
              {!hasFileLoading ? this.props.children : null}
            </div>
          </div>
          {this.state.style}
          <ModalAnchor open={this.state.isOptionsModalOpen}>
            <PrintOptionsModal onRequestClose={this._closeOptionsModal} onRequestPrint={this._print} />
          </ModalAnchor>
        </div>
        <LoadingIndicatorWatcher
          containerRef={this.contentRef.current}
          onLoadingComplete={() => {
            datadogRum.addAction('notebook.LoadingIndicatorWatcher.onLoadingComplete', {
              areFileResourcesLoaded: this.state.areFileResourcesLoaded,
            });
            if (!this.state.areFileResourcesLoaded) {
              this.setState({
                areFileResourcesLoaded: true,
                isOptionsModalOpen: isBrowser && window.location.hash === '#print',
                style: PrintUtil.isHeadlessChrome()
                  ? PrintUtil.getFitToWidthStyle(
                      this.contentRef.current as Element,
                      DEFAULT_PAGE_DIMENSIONS_FOR_HEADLESS_CHROME_EXPORTS,
                      true
                    )
                  : null,
              });
            }
          }}
        />
      </>
    );
  }
}

export default PrintableWrapper;
